// QuotePanel — Core dealing view.
// 3-column layout: RFQ queue | Counterparty + Pricing | Quote Composer

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowRightLeft, Clock, AlertTriangle, CheckCircle, XCircle,
  ChevronDown, ChevronUp, DollarSign, Shield, FileText, Users, Send, X
} from 'lucide-react';
import { getStatusStyle } from '../../config/statusColors';
import { RFQ_TYPE_LABELS, DECLINE_REASONS, DECLINE_REASON_LABELS, STALE_FEED_THRESHOLD_MS } from '../../lib/lp/constants';
import { isActionable } from '../../lib/lp/quoteStateMachine';
import { deriveClientRate, computeCounterAmount, checkMarginBand } from '../../lib/lp/pricing';

// ─── Helpers ────────────────────────────────────────────────────────────
const formatCurrency = (val) => {
  if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
  return val?.toLocaleString?.() || '0';
};
const formatRate = (r) => r != null ? Number(r).toFixed(4) : '—';
const formatPct = (val) => `${(val * 100).toFixed(2)}%`;

const getStatusBadge = (status) => {
  const style = getStatusStyle(status);
  return {
    className: 'inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border rounded-none',
    style: { ...style, borderColor: style.color },
  };
};

const Countdown = ({ deadline }) => {
  const [timeLeft, setTimeLeft] = useState('');
  useEffect(() => {
    const calc = () => {
      const diff = new Date(deadline).getTime() - Date.now();
      if (diff <= 0) return setTimeLeft('EXPIRED');
      const m = Math.floor(diff / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${m}:${String(s).padStart(2, '0')}`);
    };
    calc();
    const t = setInterval(calc, 1000);
    return () => clearInterval(t);
  }, [deadline]);

  const diff = new Date(deadline).getTime() - Date.now();
  const urgent = diff > 0 && diff < 120000;
  return (
    <span className={`font-mono text-[10px] ${urgent ? 'text-red-700 font-bold' : diff <= 0 ? 'text-red-700' : 'text-zinc-500'}`}>
      {timeLeft}
    </span>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────
const QuotePanel = ({
  rfqs, quotes, counterparties, priceFeed, cbReference, bookContext,
  competitiveContext, dealerLimits, pairFilter,
  onSubmitQuote, onAmendQuote, onWithdrawQuote, onDeclineRfq,
  onGetCounterparty, onGetPreTradeChecks, onGetCompetitiveCtx,
}) => {
  const [selectedRfqId, setSelectedRfqId] = useState(null);
  const [margin, setMargin] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [showDecline, setShowDecline] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [preTradeChecks, setPreTradeChecks] = useState([]);
  const [counterpartyData, setCounterpartyData] = useState(null);
  const rfqListRef = useRef(null);

  // Filter RFQs
  const filteredRfqs = rfqs.filter(r => {
    if (pairFilter && pairFilter !== 'all' && r.currencyPair !== pairFilter) return false;
    return true;
  });

  const liveRfqs = filteredRfqs.filter(r => r.status === 'live' || r.status === 'quoted');
  const otherRfqs = filteredRfqs.filter(r => r.status !== 'live' && r.status !== 'quoted');

  const selectedRfq = rfqs.find(r => r.id === selectedRfqId);
  const feed = selectedRfq ? priceFeed?.[selectedRfq.currencyPair] : null;
  const cbRate = selectedRfq ? cbReference?.rates?.[selectedRfq.currencyPair] : null;
  const feedStale = feed ? (Date.now() - feed.timestamp > STALE_FEED_THRESHOLD_MS) : true;

  // Get quotes for selected RFQ
  const rfqQuotes = quotes.filter(q => q.rfqId === selectedRfqId);
  const liveQuote = rfqQuotes.find(q => isActionable(q.status));

  // Load counterparty and checks when RFQ selected
  useEffect(() => {
    if (!selectedRfq) {
      setCounterpartyData(null);
      setPreTradeChecks([]);
      return;
    }

    const loadData = async () => {
      const cp = await onGetCounterparty(selectedRfq.counterpartyId);
      setCounterpartyData(cp);
      const checks = onGetPreTradeChecks(selectedRfq.id);
      setPreTradeChecks(checks);
      if (selectedRfq.type === 'competitive') {
        onGetCompetitiveCtx(selectedRfq.amount);
      }
    };
    loadData();
  }, [selectedRfqId, selectedRfq?.counterpartyId]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA') return;

      const currentIdx = liveRfqs.findIndex(r => r.id === selectedRfqId);
      if (e.key === 'ArrowDown' || e.key === 'j') {
        e.preventDefault();
        const nextIdx = Math.min(currentIdx + 1, liveRfqs.length - 1);
        if (liveRfqs[nextIdx]) setSelectedRfqId(liveRfqs[nextIdx].id);
      } else if (e.key === 'ArrowUp' || e.key === 'k') {
        e.preventDefault();
        const prevIdx = Math.max(currentIdx - 1, 0);
        if (liveRfqs[prevIdx]) setSelectedRfqId(liveRfqs[prevIdx].id);
      } else if ((e.key === 'Enter') && selectedRfqId && !showDecline) {
        e.preventDefault();
        // Focus margin input
        const input = document.getElementById('margin-input');
        if (input) input.focus();
      } else if (e.key === 'd' || e.key === 'D') {
        if (selectedRfqId) {
          e.preventDefault();
          setShowDecline(true);
        }
      } else if (e.key === 'Escape') {
        setShowDecline(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedRfqId, liveRfqs, showDecline]);

  // Auto-select first RFQ
  useEffect(() => {
    if (!selectedRfqId && liveRfqs.length > 0) {
      setSelectedRfqId(liveRfqs[0].id);
    }
  }, [liveRfqs.length]);

  // Computed pricing
  const mid = feed?.mid || 0;
  const marginNum = parseFloat(margin) || 0;
  const clientRate = selectedRfq ? deriveClientRate(mid, marginNum, selectedRfq.side) : 0;
  const counterAmount = selectedRfq ? computeCounterAmount(selectedRfq.amount, clientRate) : 0;
  const marginPct = mid > 0 ? Math.abs(marginNum / mid) : 0;
  const bandCheck = selectedRfq ? checkMarginBand(selectedRfq.amount, marginPct) : { inBand: true };
  const hasBlockingCheck = preTradeChecks.some(c => !c.passed && c.blocking);

  const handleSubmit = async () => {
    if (!selectedRfq || !marginNum || hasBlockingCheck) return;
    setSubmitting(true);
    try {
      await onSubmitQuote({ rfqId: selectedRfq.id, margin: marginNum, side: selectedRfq.side });
      setMargin('');
    } catch (err) {
      console.error('Submit quote failed:', err);
    }
    setSubmitting(false);
  };

  const handleAmend = async (quoteId) => {
    if (!marginNum) return;
    setSubmitting(true);
    try {
      await onAmendQuote(quoteId, { margin: marginNum });
      setMargin('');
    } catch (err) {
      console.error('Amend quote failed:', err);
    }
    setSubmitting(false);
  };

  const handleDecline = async () => {
    if (!selectedRfq || !declineReason) return;
    await onDeclineRfq(selectedRfq.id, declineReason);
    setShowDecline(false);
    setDeclineReason('');
    setSelectedRfqId(null);
  };

  // ─── Render ───────────────────────────────────────────────────────────
  return (
    <div className="flex gap-4 h-[calc(100vh-220px)] min-h-[500px]">
      {/* LEFT: RFQ Queue */}
      <div className="w-72 flex-shrink-0 bg-white border border-zinc-200 rounded-none flex flex-col">
        <div className="p-3 border-b border-zinc-100">
          <h3 className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
            RFQ Queue ({liveRfqs.length})
          </h3>
        </div>
        <div ref={rfqListRef} className="flex-1 overflow-y-auto">
          {liveRfqs.length === 0 && (
            <div className="p-6 text-center text-zinc-400 text-xs">
              <ArrowRightLeft className="w-6 h-6 mx-auto mb-2" />
              No live RFQs
            </div>
          )}
          {liveRfqs.map((rfq) => (
            <button
              key={rfq.id}
              onClick={() => setSelectedRfqId(rfq.id)}
              className={`w-full text-left p-3 border-b border-zinc-50 hover:bg-zinc-50 transition-colors ${
                selectedRfqId === rfq.id ? 'bg-zinc-100 border-l-2 border-l-zinc-600' : ''
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-xs font-bold">{rfq.currencyPair}</span>
                <Countdown deadline={rfq.deadline} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-zinc-500">{formatCurrency(rfq.amount)}</span>
                <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-400">
                  {RFQ_TYPE_LABELS[rfq.type] || rfq.type}
                </span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-[10px] text-zinc-400 truncate max-w-[120px]">{rfq.id}</span>
                <span {...getStatusBadge(rfq.status)}>{rfq.status}</span>
              </div>
            </button>
          ))}

          {otherRfqs.length > 0 && (
            <>
              <div className="p-2 bg-zinc-50 border-b border-zinc-100">
                <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-400">
                  Closed ({otherRfqs.length})
                </span>
              </div>
              {otherRfqs.slice(0, 5).map((rfq) => (
                <button
                  key={rfq.id}
                  onClick={() => setSelectedRfqId(rfq.id)}
                  className={`w-full text-left p-2 border-b border-zinc-50 hover:bg-zinc-50 opacity-60 ${
                    selectedRfqId === rfq.id ? 'bg-zinc-100 border-l-2 border-l-zinc-400' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px]">{rfq.currencyPair} {formatCurrency(rfq.amount)}</span>
                    <span {...getStatusBadge(rfq.status)}>{rfq.status}</span>
                  </div>
                </button>
              ))}
            </>
          )}
        </div>
        <div className="p-2 border-t border-zinc-100 text-center">
          <span className="font-mono text-[9px] text-zinc-400">
            {'\u2191\u2193'} Navigate &bull; Enter Select &bull; D Decline
          </span>
        </div>
      </div>

      {/* CENTER: Counterparty + Pricing */}
      <div className="flex-1 bg-white border border-zinc-200 rounded-none flex flex-col overflow-y-auto">
        {!selectedRfq ? (
          <div className="flex-1 flex items-center justify-center text-zinc-400 text-sm">
            Select an RFQ from the queue
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {/* RFQ Header */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-mono text-sm font-bold">{selectedRfq.id}</h3>
                <p className="text-xs text-zinc-500">
                  {selectedRfq.currencyPair} &bull; {selectedRfq.side.toUpperCase()} &bull; {formatCurrency(selectedRfq.amount)} &bull; {RFQ_TYPE_LABELS[selectedRfq.type]}
                </p>
              </div>
              <div className="text-right">
                <span {...getStatusBadge(selectedRfq.status)}>{selectedRfq.status}</span>
                <div className="mt-1">
                  <Countdown deadline={selectedRfq.deadline} />
                </div>
              </div>
            </div>

            {/* Counterparty Card */}
            {counterpartyData && (
              <div className="border border-zinc-200 rounded-none p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-zinc-400" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Counterparty</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="font-medium text-zinc-800">{counterpartyData.name}</p>
                    <p className="text-zinc-500">{counterpartyData.type} &bull; {counterpartyData.tier}</p>
                  </div>
                  <div className="text-right">
                    <span {...getStatusBadge(counterpartyData.kycStatus)}>
                      {counterpartyData.kycStatus}
                    </span>
                  </div>
                  <div>
                    <p className="font-mono text-[10px] text-zinc-400">Credit</p>
                    <p className="font-mono text-xs">
                      {formatCurrency(counterpartyData.creditUsed)} / {formatCurrency(counterpartyData.creditLimit)}
                    </p>
                  </div>
                  <div>
                    <p className="font-mono text-[10px] text-zinc-400">SSI</p>
                    <p className="font-mono text-[10px] truncate">{counterpartyData.ssi?.bank}</p>
                  </div>
                </div>
                {/* Documents */}
                <div className="mt-2 flex items-center gap-1 flex-wrap">
                  <FileText className="w-3 h-3 text-zinc-400" />
                  {(counterpartyData.documents || []).map(d => (
                    <span key={d.type} className="px-1.5 py-0.5 bg-zinc-100 text-[9px] font-mono rounded-none">
                      {d.label}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Pricing Workspace */}
            <div className="border border-zinc-200 rounded-none p-3">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-zinc-400" />
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Pricing</span>
                {feedStale && (
                  <span className="text-[9px] text-red-600 font-mono flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> STALE
                  </span>
                )}
              </div>
              <div className="grid grid-cols-4 gap-3 text-xs">
                <div>
                  <p className="font-mono text-[10px] text-zinc-400">Bid</p>
                  <p className="font-mono text-sm font-bold">{formatRate(feed?.bid)}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] text-zinc-400">Mid</p>
                  <p className="font-mono text-sm font-bold">{formatRate(feed?.mid)}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] text-zinc-400">Ask</p>
                  <p className="font-mono text-sm font-bold">{formatRate(feed?.ask)}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] text-zinc-400">Latency</p>
                  <p className="font-mono text-sm">{feed?.latencyMs || '—'}ms</p>
                </div>
              </div>
              {cbRate && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="font-mono text-[10px] text-zinc-400">CBK Ref:</span>
                  <span className="font-mono text-xs font-medium">{formatRate(cbRate)}</span>
                </div>
              )}

              {/* Competitive Context */}
              {competitiveContext && selectedRfq.type === 'competitive' && (
                <div className="mt-2 border-t border-zinc-100 pt-2">
                  <p className="font-mono text-[10px] text-zinc-400 mb-1">Competitive Context</p>
                  <div className="grid grid-cols-3 gap-2 text-[10px]">
                    <div>
                      <p className="text-zinc-400">Win Rate</p>
                      <p className="font-mono font-medium">{(competitiveContext.winRate * 100).toFixed(0)}%</p>
                    </div>
                    <div>
                      <p className="text-zinc-400">Avg Win Margin</p>
                      <p className="font-mono font-medium">{formatPct(competitiveContext.avgWinMargin)}</p>
                    </div>
                    <div>
                      <p className="text-zinc-400">Sample</p>
                      <p className="font-mono font-medium">{competitiveContext.sampleSize}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Pre-Trade Checks */}
            <div className="border border-zinc-200 rounded-none p-3">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-zinc-400" />
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Pre-Trade Checks</span>
              </div>
              <div className="space-y-1">
                {preTradeChecks.map((check, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs">
                    {check.passed ? (
                      <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0" />
                    ) : check.blocking ? (
                      <XCircle className="w-3 h-3 text-red-600 flex-shrink-0" />
                    ) : (
                      <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />
                    )}
                    <span className={`flex-1 ${check.blocking && !check.passed ? 'text-red-700 font-medium' : 'text-zinc-600'}`}>
                      {check.message}
                    </span>
                  </div>
                ))}
                {preTradeChecks.length === 0 && (
                  <p className="text-xs text-zinc-400">Loading checks...</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* RIGHT: Quote Composer */}
      <div className="w-80 flex-shrink-0 bg-white border border-zinc-200 rounded-none flex flex-col">
        <div className="p-3 border-b border-zinc-100">
          <h3 className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Quote Composer</h3>
        </div>

        {selectedRfq && (selectedRfq.status === 'live' || selectedRfq.status === 'quoted') ? (
          <div className="p-3 space-y-3 flex-1 overflow-y-auto">
            {/* Margin Input */}
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1">
                Margin ({selectedRfq.currencyPair.split('/')[1]} pts)
              </label>
              <input
                id="margin-input"
                type="number"
                step="0.01"
                value={margin}
                onChange={(e) => setMargin(e.target.value)}
                placeholder="e.g. 0.15"
                className="w-full px-3 py-2 border border-zinc-200 rounded-none text-sm font-mono focus:outline-none focus:border-zinc-400"
              />
              {marginNum > 0 && !bandCheck.inBand && (
                <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Off-policy margin ({formatPct(marginPct)} vs {formatPct(bandCheck.band?.minMargin)}-{formatPct(bandCheck.band?.maxMargin)})
                </p>
              )}
            </div>

            {/* Rate Preview */}
            {marginNum > 0 && (
              <div className="border border-zinc-200 rounded-none p-3 bg-zinc-50">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="font-mono text-[10px] text-zinc-400">Mid Rate</p>
                    <p className="font-mono font-medium">{formatRate(mid)}</p>
                  </div>
                  <div>
                    <p className="font-mono text-[10px] text-zinc-400">Client Rate</p>
                    <p className="font-mono font-bold text-lg">{formatRate(clientRate)}</p>
                  </div>
                  <div>
                    <p className="font-mono text-[10px] text-zinc-400">Base Amount</p>
                    <p className="font-mono font-medium">{formatCurrency(selectedRfq.amount)}</p>
                  </div>
                  <div>
                    <p className="font-mono text-[10px] text-zinc-400">Counter Amount</p>
                    <p className="font-mono font-medium">{counterAmount.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Submit / Amend Buttons */}
            <div className="space-y-2">
              {!liveQuote ? (
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !marginNum || hasBlockingCheck}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-zinc-700 text-white text-xs font-mono uppercase tracking-wider hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-none transition-colors"
                >
                  <Send className="w-4 h-4" />
                  {submitting ? 'Submitting...' : 'Submit Quote'}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => handleAmend(liveQuote.id)}
                    disabled={submitting || !marginNum}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-zinc-600 text-white text-xs font-mono uppercase tracking-wider hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-none transition-colors"
                  >
                    {submitting ? 'Amending...' : 'Amend Quote'}
                  </button>
                  <button
                    onClick={() => onWithdrawQuote(liveQuote.id)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-zinc-300 text-zinc-600 text-xs font-mono uppercase tracking-wider hover:bg-zinc-50 rounded-none transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Withdraw
                  </button>
                </>
              )}

              {hasBlockingCheck && (
                <p className="text-[10px] text-red-600 text-center">
                  Cannot quote: blocking pre-trade check(s) failed
                </p>
              )}
            </div>

            {/* Decline Section */}
            {showDecline && (
              <div className="border border-zinc-200 rounded-none p-3 bg-red-50">
                <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-red-700 mb-2">Decline RFQ</p>
                <select
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  className="w-full border border-zinc-200 rounded-none px-2 py-1 text-xs font-mono mb-2 focus:outline-none"
                >
                  <option value="">Select reason...</option>
                  {Object.entries(DECLINE_REASON_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
                <div className="flex gap-2">
                  <button
                    onClick={handleDecline}
                    disabled={!declineReason}
                    className="flex-1 px-3 py-1 bg-red-600 text-white text-xs font-mono uppercase rounded-none hover:bg-red-700 disabled:opacity-50"
                  >
                    Confirm Decline
                  </button>
                  <button
                    onClick={() => { setShowDecline(false); setDeclineReason(''); }}
                    className="px-3 py-1 border border-zinc-200 text-xs font-mono uppercase rounded-none hover:bg-zinc-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {!showDecline && selectedRfq.status === 'live' && (
              <button
                onClick={() => setShowDecline(true)}
                className="w-full px-4 py-1.5 border border-zinc-200 text-zinc-500 text-xs font-mono uppercase tracking-wider hover:bg-zinc-50 rounded-none transition-colors"
              >
                Decline RFQ
              </button>
            )}

            {/* Live Quotes for this RFQ */}
            {rfqQuotes.length > 0 && (
              <div className="border-t border-zinc-100 pt-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">
                  Quotes ({rfqQuotes.length})
                </p>
                {rfqQuotes.map(q => (
                  <div key={q.id} className="border border-zinc-100 rounded-none p-2 mb-1">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px]">{q.id}</span>
                      <span {...getStatusBadge(q.status)}>{q.status}</span>
                    </div>
                    <div className="flex items-center justify-between mt-1 text-[10px] text-zinc-500">
                      <span>Rate: {formatRate(q.clientRate)}</span>
                      <span>Margin: {q.margin}</span>
                    </div>
                    {isActionable(q.status) && q.expiresAt && (
                      <div className="mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3 text-zinc-400" />
                        <Countdown deadline={q.expiresAt} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-zinc-400 text-xs p-4 text-center">
            {selectedRfq ? 'RFQ is no longer live' : 'Select a live RFQ to compose a quote'}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuotePanel;
